import personalPickupsQueries from '@/api/modules/personalPickupsQueries'
import { notificationService } from '@/services/notificationService'
import { t } from '@/i18n'
import { handleApiError } from '@/utils/apiHelpers'

import type { AxiosResponse } from 'axios'
import type {
    PersonalPickupsListResponse,
    PersonalPickupDetailResponse,
    CreatePersonalPickupRequest,
    CreatePersonalPickupForPackageRequest,
    UpdatePersonalPickupRequest,
    PersonalPickupOrdersListResponse,
    PersonalPickupPackagesListResponse
} from '@/api/schemas/shipments/personalPickups'
import type { PersonalPickupsFilters } from '@/types/domain/shipments/personalPickups'
import type { PersonalPickupsState } from './state'
import { createInitialFilters } from './state'

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export function createPersonalPickupsActions(state: PersonalPickupsState) {
    const fetchPersonalPickups = async (queryString = ''): Promise<AxiosResponse<PersonalPickupsListResponse>> => {
        try {
            state.personalPickupsLoading.value = true
            const response = await personalPickupsQueries.fetchPersonalPickups(queryString)
            state.personalPickups.value = response?.data?.results || []
            return response
        } catch (error) {
            handleApiError(error, t('shipments.personalPickups.errors.fetchList'))
            throw error
        } finally {
            state.personalPickupsLoading.value = false
        }
    }

    const fetchPersonalPickup = async (id: number): Promise<AxiosResponse<PersonalPickupDetailResponse>> => {
        try {
            return await personalPickupsQueries.fetchPersonalPickup(id)
        } catch (error) {
            handleApiError(error, t('shipments.personalPickups.errors.fetchDetail', { id }))
            throw error
        }
    }

    const prefetchPersonalPickup = async (id: number): Promise<AxiosResponse<PersonalPickupDetailResponse>> => {
        const response = await fetchPersonalPickup(id)
        state.prefetchedPersonalPickup.value = response.data
        return response
    }

    const consumePrefetchedPersonalPickup = (id: number): PersonalPickupDetailResponse | null => {
        const prefetchedPersonalPickup = state.prefetchedPersonalPickup.value

        if (!prefetchedPersonalPickup || prefetchedPersonalPickup.id !== id) {
            return null
        }

        state.prefetchedPersonalPickup.value = null
        return prefetchedPersonalPickup
    }

    const createPersonalPickup = async (
        payload: CreatePersonalPickupRequest
    ): Promise<AxiosResponse<PersonalPickupDetailResponse>> => {
        try {
            const response = await personalPickupsQueries.createPersonalPickup(payload)
            notificationService.success(t('shipments.personalPickups.notifications.created'))
            return response
        } catch (error) {
            handleApiError(error, t('shipments.personalPickups.errors.create'))
            throw error
        }
    }

    const createPersonalPickupForPackage = async (
        payload: CreatePersonalPickupForPackageRequest
    ): Promise<AxiosResponse<PersonalPickupDetailResponse>> => {
        try {
            const response = await personalPickupsQueries.createPersonalPickupForPackage(payload)
            notificationService.success(t('shipments.personalPickups.notifications.created'))
            return response
        } catch (error) {
            handleApiError(error, t('shipments.personalPickups.errors.create'))
            throw error
        }
    }

    const updatePersonalPickup = async (
        id: number,
        payload: UpdatePersonalPickupRequest
    ): Promise<AxiosResponse<PersonalPickupDetailResponse>> => {
        try {
            const response = await personalPickupsQueries.updatePersonalPickup(id, payload)
            notificationService.success(t('shipments.personalPickups.notifications.updated'))
            return response
        } catch (error) {
            handleApiError(error, t('shipments.personalPickups.errors.update'))
            throw error
        }
    }

    const deletePersonalPickup = async (id: number): Promise<AxiosResponse<void>> => {
        try {
            const response = await personalPickupsQueries.deletePersonalPickup(id)
            notificationService.success(t('shipments.personalPickups.notifications.deleted'))
            return response
        } catch (error) {
            handleApiError(error, t('shipments.personalPickups.errors.delete'))
            throw error
        }
    }

    const fetchPersonalPickupsOrders = async (queryString = ''): Promise<AxiosResponse<PersonalPickupOrdersListResponse>> => {
        try {
            return await personalPickupsQueries.fetchPersonalPickupsOrders(queryString)
        } catch (error) {
            handleApiError(error, t('shipments.personalPickups.errors.fetchOrders'))
            throw error
        }
    }

    const fetchPackages = async (queryString = ''): Promise<AxiosResponse<PersonalPickupPackagesListResponse>> => {
        try {
            return await personalPickupsQueries.fetchPackages(queryString)
        } catch (error) {
            handleApiError(error, t('shipments.personalPickups.errors.fetchPackages'))
            throw error
        }
    }

    const setFilters = (payload: Partial<PersonalPickupsFilters>): void => {
        state.filters.value = {
            ...state.filters.value,
            ...payload
        }
    }

    const resetFilters = (): void => {
        state.filters.value = createInitialFilters()
    }

    return {
        fetchPersonalPickups,
        fetchPersonalPickup,
        prefetchPersonalPickup,
        consumePrefetchedPersonalPickup,
        createPersonalPickup,
        createPersonalPickupForPackage,
        updatePersonalPickup,
        deletePersonalPickup,
        fetchPersonalPickupsOrders,
        fetchPackages,
        setFilters,
        resetFilters,
        createInitialFilters
    }
}
